import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Clipboard, Printer, ArrowLeft, Wrench, Package } from "lucide-react";

const fmtEUR = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

export default function SiteOrders() {
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [workItems, setWorkItems] = useState([]);
  const [company, setCompany] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      const [q, c, w, co] = await Promise.all([
        api.get("/quotes"), api.get("/clients"), api.get("/work-items"), api.get("/company"),
      ]);
      setQuotes(q.data.filter((x) => x.status === "accepte" || x.status === "facture"));
      setClients(c.data);
      setWorkItems(w.data.items);
      setCompany(co.data);
    })();
  }, []);

  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
  const wiMap = Object.fromEntries(workItems.map((w) => [w.id, w]));

  if (selected) {
    return <SiteOrderSheet quote={selected} client={clientMap[selected.client_id]} company={company} wiMap={wiMap} onBack={() => setSelected(null)} />;
  }

  return (
    <div data-testid="site-orders-page" className="max-w-7xl mx-auto">
      <header className="mb-8">
        <div className="flex items-center gap-2 text-xs font-semibold text-[#FFC400] mb-3"><Clipboard size={13} /> Module 6 — Fiches chantier</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">Fiches chantier</h1>
        <p className="text-secondary-text mt-3">Pour chaque devis accepté, générez une fiche A4 imprimable avec matières, outillage et notes terrain.</p>
      </header>

      <div className="card overflow-hidden">
        {quotes.length === 0 ? (
          <div className="p-16 text-center text-tertiary-text">
            <Clipboard size={36} className="mx-auto mb-3 opacity-40" />
            <div className="font-medium text-primary-text">Aucun devis accepté</div>
            <div className="text-sm">Acceptez d'abord un devis dans le module Devis.</div>
          </div>
        ) : (
          <table className="table">
            <thead><tr><th>Devis</th><th>Client</th><th>Chantier</th><th className="text-right">Total HT</th><th></th></tr></thead>
            <tbody>
              {quotes.map((q) => {
                const c = clientMap[q.client_id];
                return (
                  <tr key={q.id} className="cursor-pointer" onClick={() => setSelected(q)} data-testid={`so-${q.id}`}>
                    <td className="font-mono text-xs">{q.number}</td>
                    <td>{c?.last_name} {c?.first_name}</td>
                    <td>{q.project_name || "—"}</td>
                    <td className="text-right font-mono">{fmtEUR(q.total_ht)}</td>
                    <td className="text-right"><span className="text-[#FFC400] text-sm">Voir la fiche →</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SiteOrderSheet({ quote, client, company, wiMap, onBack }) {
  // Aggregated materials list
  const materials = {};
  const tools = new Set();
  let totalHours = 0;
  quote.lines.forEach((l) => {
    const wi = wiMap[l.work_item_id];
    if (!wi) return;
    totalHours += (wi.labor_hours_per_unit || 0) * (l.quantity || 0);
    (wi.materials || []).forEach((m) => {
      const k = `${m.name}__${m.unit}`;
      materials[k] = materials[k] || { name: m.name, unit: m.unit, qty: 0 };
      materials[k].qty += (m.quantity || 0) * (l.quantity || 0);
    });
    (wi.tools || []).forEach((t) => tools.add(t));
  });

  return (
    <div className="max-w-4xl mx-auto" data-testid="site-order-sheet">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <button onClick={onBack} className="btn btn-ghost"><ArrowLeft size={14} /> Retour à la liste</button>
        <button onClick={() => window.print()} className="btn btn-primary" data-testid="print-sheet"><Printer size={14} /> Imprimer</button>
      </div>

      <div className="bg-[var(--surface)] shadow-lg p-10 print:shadow-none print:p-0" id="print-area">
        <header className="flex items-start justify-between border-b-2 border-[var(--carbon-3)] pb-5 mb-6 print:pb-2 print:mb-3">
          <div>
            <div className="text-[11px] font-semibold text-[#FFC400] mb-1">Fiche chantier</div>
            <h1 className="font-display text-3xl print:text-xl">{quote.project_name || "Chantier"}</h1>
            <div className="text-sm text-tertiary-text mt-1 print:text-[10px]">Réf. devis : {quote.number}</div>
          </div>
          <div className="text-right">
            <div className="font-bold print:text-xs">{company?.name}</div>
            <div className="text-xs text-tertiary-text print:text-[9px]">{company?.address}</div>
            <div className="text-xs text-tertiary-text print:text-[9px]">{company?.phone} · {company?.email}</div>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-6 mb-6 print:mb-3 print:gap-3">
          <div>
            <div className="text-[10px]  text-tertiary-text font-semibold mb-2 print:mb-1">Client</div>
            <div className="font-semibold print:text-xs">{client?.last_name} {client?.first_name}</div>
            {client?.company_name && <div className="text-sm print:text-[11px]">{client.company_name}</div>}
            <div className="text-sm text-secondary-text mt-1 print:text-[10px] print:mt-0">{client?.address}<br/>{client?.postal_code} {client?.city}</div>
            <div className="text-sm text-secondary-text mt-1 print:text-[10px] print:mt-0">{client?.phone}</div>
          </div>
          <div>
            <div className="text-[10px]  text-tertiary-text font-semibold mb-2 print:mb-1">Intervention</div>
            <div className="text-sm print:text-[11px]">Temps total estimé : <b>{totalHours.toFixed(1)} h</b></div>
            <div className="text-sm print:text-[11px]">Trajets prévus : <b>{quote.trips_count || 0}</b> ({quote.total_km || 0} km)</div>
            <div className="text-sm print:text-[11px]">Date d&apos;intervention : ____________________</div>
            <div className="text-sm mt-2 print:text-[11px] print:mt-0">Technicien : ____________________</div>
          </div>
        </section>

        <section className="mb-6 print:mb-3">
          <h2 className="font-bold text-sm  border-b border-stone-200 pb-1 mb-3 print:mb-1 print:text-[11px]">Travaux à réaliser</h2>
          <table className="w-full text-sm print:text-[10px]">
            <thead><tr className="border-b border-stone-200 text-[10px]  text-tertiary-text print:text-[9px]">
              <th className="text-left py-2 print:py-0.5">Code</th><th className="text-left">Description</th><th className="text-right">Qté</th><th>Unité</th>
            </tr></thead>
            <tbody>
              {quote.lines.map((l, i) => (
                <tr key={i} className="border-b border-stone-100">
                  <td className="py-2 font-mono text-xs print:py-0.5 print:text-[9px]">{l.code}</td>
                  <td className="print:py-0.5">{l.description}</td>
                  <td className="text-right font-mono print:py-0.5">{l.quantity}</td>
                  <td className="print:py-0.5">{l.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="mb-6 grid grid-cols-2 gap-6 print:mb-3 print:gap-3">
          <div>
            <h2 className="font-bold text-sm  border-b border-stone-200 pb-1 mb-3 flex items-center gap-2 print:mb-1 print:text-[11px]"><Package size={14} /> Matières à prévoir</h2>
            <ul className="text-sm space-y-1 print:text-[9px] print:space-y-0 print:leading-tight">
              {Object.values(materials).map((m, i) => (
                <li key={i} className="flex justify-between border-b border-stone-100 py-1 print:py-0 print:border-none"><span>{m.name}</span><span className="font-mono">{m.qty.toFixed(2)} {m.unit}</span></li>
              ))}
              {Object.keys(materials).length === 0 && <li className="text-tertiary-text">—</li>}
            </ul>
          </div>
          <div>
            <h2 className="font-bold text-sm  border-b border-stone-200 pb-1 mb-3 flex items-center gap-2 print:mb-1 print:text-[11px]"><Wrench size={14} /> Outillage requis</h2>
            <ul className="text-sm space-y-1 print:text-[9px] print:space-y-0 print:leading-tight print:grid print:grid-cols-2 print:gap-x-3">
              {Array.from(tools).map((t, i) => <li key={i} className="border-b border-stone-100 py-1 print:py-0 print:border-none">□ {t}</li>)}
              {tools.size === 0 && <li className="text-tertiary-text">—</li>}
            </ul>
          </div>
        </section>

        <section>
          <h2 className="font-bold text-sm  border-b border-stone-200 pb-1 mb-3 print:mb-1 print:text-[11px]">Notes terrain & signature</h2>
          <div className="h-24 border border-stone-200 rounded p-2 text-xs text-tertiary-text print:h-8 print:text-[9px] print:p-1">Observations particulières (étage, code accès, animaux, etc.)</div>
          <div className="grid grid-cols-2 gap-6 mt-4 text-sm print:mt-1 print:gap-3">
            <div><div className="text-tertiary-text text-xs print:text-[9px]">Signature technicien</div><div className="h-16 border-b border-stone-300 mt-2 print:h-5 print:mt-1"></div></div>
            <div></div>
          </div>
        </section>
      </div>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          html, body { background: #fff !important; }
          body * { visibility: hidden; }
          #print-area, #print-area * { visibility: visible; }
          #print-area {
            position: absolute;
            left: 0; top: 0;
            width: 100%;
            padding: 0 !important;
            box-shadow: none !important;
            color: #000 !important;
            background: #fff !important;
            font-size: 11px;
            line-height: 1.3;
          }
          #print-area h1, #print-area h2 { color: #000 !important; }
          #print-area header { padding-bottom: 4px !important; margin-bottom: 6px !important; }
          #print-area table { page-break-inside: avoid; }
          #print-area .text-tertiary-text,
          #print-area .text-secondary-text { color: #555 !important; }
          #print-area .border-stone-200,
          #print-area .border-stone-300,
          #print-area .border-stone-100 { border-color: #ccc !important; }
        }
      `}</style>
    </div>
  );
}
